import React from 'react';
import { createBrowserRouter, RouterProvider, createRoutesFromElements, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Header from './components/Header';
import { Login, Verify } from './pages/Login';
import Store from './pages/Store/Store';
import StoreSelection from './pages/StoreSelection/StoreSelection';
import NoStores from './pages/NoStores/NoStores';
import { createApi } from './utils/api';
import { authService } from './data';
import { log } from './utils/logger';
import {
  resolveUserStores,
  navigateForStores,
  shouldRedirectForStores,
  withStores,
} from './utils/storeRouting';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #F9F6F2;
`;

const MainContent = styled.main`
  flex: 1;
`;

const Layout = () => (
  <AppContainer>
    <Header />
    <MainContent>
      <Outlet />
    </MainContent>
  </AppContainer>
);

// Create auth context
export const AuthContext = React.createContext(null);

// Enable React Router v7 features
const routerOptions = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

// Auth provider to manage global auth state
const AuthProvider = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthed, setIsAuthed] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isApiReady, setIsApiReady] = React.useState(false);
  const [stores, setStores] = React.useState([]);
  const [user, setUser] = React.useState(null);

  // Move useMemo to the top, before any other logic
  const value = React.useMemo(() => ({
    isAuthed,
    isLoading,
    stores,
    user,
    setUser,
    setStores,
    setIsAuthed
  }), [isAuthed, isLoading, stores, user]);

  // Track if auth check has been done
  const authCheckRef = React.useRef(false);

  // Initialize API first
  React.useEffect(() => {
    try {
      createApi(navigate);
      setIsApiReady(true);
    } catch (error) {
      console.error('Failed to initialize API:', error);
    }
  }, [navigate]);

  // Only run auth check after API is ready
  React.useEffect(() => {
    if (!isApiReady) return;

    // Only run auth check if NOT on the verify route
    if (location.pathname.startsWith('/login/verify/token/')) {
      return;
    }

    const checkAuth = async () => {
      // Skip if auth check already done
      if (authCheckRef.current) return;
      authCheckRef.current = true;

      try {
        const { success, isAuthenticated, user: loginUser } = await authService.initiateLogin();
        
        if (success && isAuthenticated) {
          setIsAuthed(true);

          let resolvedStores = [];
          let storesResolved = false;
          try {
            resolvedStores = await resolveUserStores(loginUser);
            storesResolved = true;
          } catch (storeError) {
            // Prefer nested stores if the fallback fetch fails; do not treat
            // a fetch error as "no stores".
            log('Store resolution error during auth check:', storeError);
            if (loginUser && Array.isArray(loginUser.stores) && loginUser.stores.length > 0) {
              resolvedStores = loginUser.stores;
              storesResolved = true;
            } else {
              resolvedStores = Array.isArray(loginUser?.stores) ? loginUser.stores : [];
            }
          }

          const hydratedUser = withStores(loginUser, resolvedStores);
          setUser(hydratedUser);
          setStores(resolvedStores);

          if (shouldRedirectForStores(location.pathname)) {
            if (resolvedStores.length > 0 || storesResolved) {
              navigateForStores(navigate, resolvedStores);
            }
          }
        } else {
          setIsAuthed(false);
          setUser(null);
          setStores([]);
          if (location.pathname !== '/login' && !location.pathname.startsWith('/login/verify')) {
            navigate('/login');
          }
        }
      } catch (error) {
        log('Auth check error:', error);
        setIsAuthed(false);
        setUser(null);
        setStores([]);
        if (location.pathname !== '/login') {
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [isApiReady, location.pathname, navigate]);

  // Return loading state if API is not ready
  if (!isApiReady) {
    return <div>Initializing...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      <Outlet />
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protect routes that require authentication
const PrivateRoute = ({ children }) => {
  const { isAuthed, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthed) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AuthProvider />}>
      {/* Public routes */}
      <Route
        path="/login"
        element={<Login />}
      />
      <Route 
        path="/login/verify/token/:token" 
        element={<Verify />} 
      />

      {/* Protected routes */}
      <Route element={<Layout />}>
        <Route
          index
          element={<PrivateRoute><StoreSelection /></PrivateRoute>}
        />
        <Route
          path="/store/:storeId"
          element={<PrivateRoute><Store /></PrivateRoute>}
        />
        <Route
          path="/no-stores"
          element={<PrivateRoute><NoStores /></PrivateRoute>}
        />
        <Route
          path="/stores"
          element={<PrivateRoute><StoreSelection /></PrivateRoute>}
        />
      </Route>
    </Route>
  ),
  routerOptions
);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
